import { ref, toRef, watch, computed } from '#imports';
import { useMediaQuery } from '@vueuse/core';

import type { InputProps as Props } from '@/shared/interfaces';
import type { FieldVariation, InputValue } from '@/shared/types';
import VScroll from '@/shared/components/global/v-scroll.vue';

export default (props: Props<InputValue, FieldVariation>, emit: any, slots: any) => {
	const fieldTypeProps = toRef(props, 'type');
	const fieldType = ref<string | undefined>('');
	const isSmallScreen = useMediaQuery('(max-width: 1024px)');

	watch(fieldTypeProps, (val) => {
		fieldType.value = val;
	}, { immediate: true });

	const isFocus = ref(false);
	const isTextarea = computed(() => props.tag === 'textarea');
	const computedType = computed(() => isTextarea.value ? null : fieldType.value);

	const computedClass = computed(() => {
		const modificators = props.variations?.map((variation: FieldVariation) => {
			return `field--${variation}`;
		}) ?? [];

		const classes = {
			'field--focus': isFocus.value,
			'field--error': props.isError,
			'field--required': props.required,
			'field--textarea': isTextarea.value,
			'field--disabled': props.disabled,
			'field--has-action': slots.action && props.hasAction,
		};

		return [...modificators, classes];
	});

	const handleInput = (e: Event) => {
		const el = e.target as HTMLInputElement;

		if (isTextarea.value) {
			el.style.height = 'auto';
			el.style.height = `${el.scrollHeight}px`;
		}

		emit('update:modelValue', el.value);
	};

	const handlePassword = (event: MouseEvent) => {
		switch (event.type) {
			case 'mousedown':
				fieldType.value = 'text';
				break;
			default:
				fieldType.value = 'password';
				break;
		}
	};

	const togglePassword = () => {
		fieldType.value = fieldType.value === 'text' ? 'password' : 'text';
	};

	const computedListeners = computed(() => {
		const listeners: Record<string, unknown> = {};

		if (fieldTypeProps.value === 'search') {
			listeners.click = () => emit('reset-value');
		}

		if (fieldTypeProps.value === 'password') {
			if (isSmallScreen.value) {
				listeners.click = togglePassword;
			} else {
				listeners.mousedown = handlePassword;
				listeners.mouseup = handlePassword;
				listeners.mouseleave = handlePassword;
			}
		}

		return listeners;
	});

	const computedActionTag = computed(() => {
		return Object.keys(computedListeners.value).length > 0 || props.actionType === 'action'
			? 'button'
			: 'div';
	});

	const computedActionAttrs = computed(() => ({
		type: computedActionTag.value === 'button' ? 'button' : undefined,
	}));

	const computedControl = computed(() => {
		return isTextarea.value ? VScroll : 'div';
	});

	return {
		isFocus,
		isTextarea,
		computedClass,
		computedType,
		computedListeners,
		computedActionTag,
		computedActionAttrs,
		computedControl,
		handleInput,
	};
};
